import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Modal,
  Switch,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "components/Table";
import FeeModal from "components/Modals/Fee";
import ConfirmationModal from "components/Modals/Confirmation";
import { deleteFee, getFees } from "api/resources/fees";
import { Fee, feeCountryLabels, feeTypeLabels } from "api/models/Fee";
import { formatNumberWithTwoDecimals } from "helpers/numbers";
import {
  PERIOD_STATUS_ORDER,
  periodStatus,
  periodStatusChips,
} from "helpers/periodStatus";

const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";

const FEE_TYPE_ORDER = Object.keys(feeTypeLabels);
const FEE_COUNTRY_ORDER = Object.keys(feeCountryLabels);

const byStatusThenCountryThenTypeThenNewest = (a: Fee, b: Fee) =>
  PERIOD_STATUS_ORDER.indexOf(periodStatus(a)) -
    PERIOD_STATUS_ORDER.indexOf(periodStatus(b)) ||
  FEE_COUNTRY_ORDER.indexOf(a.country) - FEE_COUNTRY_ORDER.indexOf(b.country) ||
  FEE_TYPE_ORDER.indexOf(a.costType) - FEE_TYPE_ORDER.indexOf(b.costType) ||
  dayjs(b.effectiveFrom).diff(dayjs(a.effectiveFrom));

const FeesTable = () => {
  const [fees, setFees] = useState<Fee[] | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [editedFee, setEditedFee] = useState<Fee | null>(null);
  const [feeToDelete, setFeeToDelete] = useState<Fee | null>(null);
  const [showPastFees, setShowPastFees] = useState(false);

  const pastFeeCount = (fees ?? []).filter(
    (fee) => periodStatus(fee) === "PAST",
  ).length;
  const visibleFees = (fees ?? [])
    .filter((fee) => showPastFees || periodStatus(fee) !== "PAST")
    .sort(byStatusThenCountryThenTypeThenNewest);

  const loadFees = useCallback(() => {
    getFees()
      .then(setFees)
      .catch((err) => {
        console.error("Failed to load fees:", err);
        setFees([]);
      });
  }, []);

  useEffect(() => {
    loadFees();
  }, [loadFees]);

  const handleDeleteFee = (id: number) => {
    deleteFee(id)
      .then(() => {
        setFeeToDelete(null);
        loadFees();
      })
      .catch((err) => {
        console.error("Failed to delete fee:", err);
      });
  };

  const columns = [
    {
      accessorKey: "country",
      header: "Country",
      cell: (row: any) =>
        feeCountryLabels[row.row.original.country as Fee["country"]],
    },
    {
      accessorKey: "costType",
      header: "Fee",
      cell: (row: any) =>
        feeTypeLabels[row.row.original.costType as Fee["costType"]],
    },
    {
      accessorKey: "effectiveFrom",
      header: "Valid from",
      cell: (row: any) =>
        dayjs(row.row.original.effectiveFrom).format(DISPLAY_DATE_FORMAT),
    },
    {
      accessorKey: "effectiveTo",
      header: "Valid to",
      cell: (row: any) =>
        dayjs(row.row.original.effectiveTo).format(DISPLAY_DATE_FORMAT),
    },
    {
      accessorKey: "price",
      header: "Price (€/MWh)",
      cell: (row: any) => formatNumberWithTwoDecimals(row.row.original.price),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        const chip = periodStatusChips[periodStatus(row.row.original)];

        return <Chip size="small" label={chip.label} color={chip.color} />;
      },
    },
    {
      accessorKey: "actions",
      header: "",
      cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            aria-label="Edit fee"
            onClick={() => setEditedFee(row.row.original)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Delete fee"
            onClick={() => setFeeToDelete(row.row.original)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Fees</Typography>
        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPastFees}
                onChange={(event) => setShowPastFees(event.target.checked)}
              />
            }
            label={`Show past fees (${pastFeeCount})`}
            disabled={pastFeeCount === 0}
            sx={{ mr: "1.5rem" }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddModal(true)}
          >
            Add fee
          </Button>
        </Box>
      </Box>
      <Table
        data={visibleFees}
        columns={columns}
        isFetching={!fees}
        pageCount={1}
        noDataText={
          pastFeeCount > 0
            ? "No active or upcoming fees"
            : "No fees have been set"
        }
      />
      <Modal open={addModal} onClose={() => setAddModal(false)}>
        <FeeModal onSaved={loadFees} onClose={() => setAddModal(false)} />
      </Modal>
      <Modal open={editedFee !== null} onClose={() => setEditedFee(null)}>
        <FeeModal
          fee={editedFee ?? undefined}
          onSaved={loadFees}
          onClose={() => setEditedFee(null)}
        />
      </Modal>
      <Modal open={feeToDelete !== null} onClose={() => setFeeToDelete(null)}>
        <ConfirmationModal
          title="Do you want to delete this fee period?"
          subtitle="Reports for this period will no longer include the fee."
          buttonTitle="Delete"
          action={() => handleDeleteFee(feeToDelete.id)}
          setConfirmationModal={() => setFeeToDelete(null)}
        />
      </Modal>
    </>
  );
};

export default FeesTable;
